import { CurrencyPipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { Component, OnChanges, SimpleChanges, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgbDate, NgbDateStruct, NgbDatepickerModule, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { CurrencyInputDirective, DisableScrollOnInputDirective } from '@zero/shared/ui-directives';
import { IconComponent } from '@zero/shared/ui-web-design-system';
import { BootstrapTimeService } from '@zero/shared/util-bootstrap-time';
import { TradeCreateQuantityBaseComponent } from '@zero/trade/feature-trade/base';
import { MAX_ORDER_VALIDITY_DAYS } from 'constants/shared-constants';
import { take } from 'rxjs';

@Component({
  selector: 'trade-create-quantity',
  templateUrl: './trade-create-quantity.component.html',
  styleUrls: ['./trade-create-quantity.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgbDatepickerModule,
    DisableScrollOnInputDirective,
    NgTemplateOutlet,
    DecimalPipe,
    CurrencyPipe,
    TranslocoPipe,
    CurrencyInputDirective,
    IconComponent,
  ],
})
export class TradeCreateQuantityComponent extends TradeCreateQuantityBaseComponent implements OnChanges {
  public minDate?: NgbDateStruct;
  public maxDate?: NgbDateStruct;
  public isHighVolumeTooltipVisible = false;
  public isAvailableQuantityTooltipVisible = false;

  #isDatepickerClosed = true;
  readonly #bootstrapTimeService = inject(BootstrapTimeService);

  public override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    this.#initDatepickerMinMax();
  }

  public openDatepicker(_ev: FocusEvent, datepicker: NgbInputDatepicker, input: HTMLInputElement): void {
    if (datepicker && !datepicker.isOpen() && this.#isDatepickerClosed) {
      this.#isDatepickerClosed = false;
      datepicker.open();
      datepicker.closed.pipe(take(1)).subscribe(() => {
        Promise.resolve().then(() => {
          this.#isDatepickerClosed = true;
          input?.blur && input.blur();
        });
      });
    }
  }

  public isDateDisabled(): (date: NgbDate, current: unknown) => boolean {
    return (date: NgbDate) => {
      const utcDateTime = this.#bootstrapTimeService.getCurrentLocalDateTimeOffsetAdjustedFrom(date);
      return this.tradingTimesService.isWeekend(utcDateTime) || this.tradingTimesService.isHoliday(utcDateTime);
    };
  }

  public handleQuantityPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const possibleNumber = this.#determineNumber(event.clipboardData?.getData('text'));
    possibleNumber && this.form.controls.quantity.setValue(possibleNumber);
  }

  public handleValuePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const possibleNumber = this.#determineNumber(event.clipboardData?.getData('text'));
    possibleNumber && this.handleValueChange(possibleNumber.toString());
  }

  public useQty = (qty: number) => {
    this.form.controls.quantity.setValue(qty);
    this.calcValue();
  };

  #determineNumber = (input: string) => {
    input = input.replace(/\./g, '').replace(',', '.');
    const num = parseFloat(input);
    return !isNaN(num) && isFinite(num) ? num : null;
  };

  #initDatepickerMinMax(): void {
    const germanDate: Date = this.tradingTimesService.getGermanTodayDate();
    this.minDate = {
      day: germanDate.getDate(),
      month: germanDate.getMonth() + 1,
      year: germanDate.getFullYear(),
    };

    const maxGermanDate: Date = this.#addDays(germanDate, MAX_ORDER_VALIDITY_DAYS);
    this.maxDate = {
      day: maxGermanDate.getDate(),
      month: maxGermanDate.getMonth() + 1,
      year: maxGermanDate.getFullYear(),
    };
  }

  #addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }
}
